import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Wrench, ChevronRight, ChevronDown, ShieldCheck, Layers, Receipt, Check, Pencil, PhoneCall, ArrowRight, type LucideIcon,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import {
  buildEquipmentView, classifyEquipment, type ClassifiedEquip, type EquipCategory, type EquipmentRow,
  installDateLabel,
} from "@/lib/equipment";
import { equipIcon, equipStyle } from "@/lib/equipmentIcons";
import { useActiveAgreementNumbers } from "@/features/agreements/api";
import {
  useEquipmentOverrides, useSetEquipmentOverride, useClearEquipmentOverride,
  useEditEquipmentFields, REASON_LABEL, type RemovalReason,
} from "./equipmentOverrides";
import { useInstalledEquipment, useSetUnitRoom, type InstalledEquipment } from "@/features/jobs/jobs";
import { ozText } from "@/features/jobs/refrigerant";
import { useOwnerEra, PriorOwnerBadge } from "./ownerEra";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { CopyCode } from "@/components/Copyable";
import { InfoTip } from "@/components/InfoTip";
import { useCustomerDispatches, type CustomerDispatchRow } from "./api";

interface Props {
  custno: string;
  locno?: string | null;
  /** Jump to the customer's Agreements tab (from the "On agreement" badge). */
  onOpenAgreement?: () => void;
}

const SELECT =
  "counter, mfg, model, serial, eq_type, equip_loc, install_date, warranty_date, ser_agr_no, notes, locno, custom_1, custom_2, invoice";

export function useCustomerEquipment(custno: string | undefined, locno?: string | null) {
  return useQuery({
    queryKey: ["customer_equipment", custno, locno ?? "all"],
    queryFn: async (): Promise<EquipmentRow[]> => {
      if (!custno) return [];
      let q = supabase.from("mirror_equipment").select(SELECT).eq("custno", custno).limit(800);
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as EquipmentRow[];
    },
    enabled: !!custno,
    staleTime: 60 * 1000,
  });
}

/**
 * EQUIP-360-AI — for models the deterministic rules leave as "other", read (and
 * trigger) the AI classifier (equipment_model_class + classify-equipment-models)
 * so the long tail gets a real category instead of generic "Equipment". One
 * shared query feeds both the Equipment tab and the Overview summary card.
 */
export function useAiEquipmentOverrides(rows: EquipmentRow[]) {
  const unknown = useMemo(() => {
    const m = new Map<string, { mfg: string; model: string }>();
    for (const r of rows) {
      if (!r.model) continue;
      if (classifyEquipment(r).category !== "other") continue;
      const mfg = (r.mfg ?? "").toUpperCase().trim();
      const model = r.model.toUpperCase().trim();
      m.set(`${mfg}|${model}`, { mfg, model });
    }
    return [...m.values()];
  }, [rows]);

  return useQuery({
    queryKey: [
      "equip_ai_overrides",
      unknown.map((u) => `${u.mfg}|${u.model}`).sort().join(","),
    ],
    enabled: unknown.length > 0,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Record<string, EquipCategory>> => {
      const map: Record<string, EquipCategory> = {};
      const { data: cached } = await supabase
        .from("equipment_model_class")
        .select("mfg_norm, model_norm, category")
        .in(
          "model_norm",
          unknown.map((u) => u.model),
        );
      const cachedSet = new Set<string>();
      for (const c of (cached ?? []) as {
        mfg_norm: string;
        model_norm: string;
        category: string;
      }[]) {
        map[`${c.mfg_norm}|${c.model_norm}`] = c.category as EquipCategory;
        cachedSet.add(`${c.mfg_norm}|${c.model_norm}`);
      }
      const toClassify = unknown.filter(
        (u) => !cachedSet.has(`${u.mfg}|${u.model}`),
      );
      if (toClassify.length > 0) {
        const { data: res } = await supabase.functions.invoke(
          "classify-equipment-models",
          { body: { items: toClassify } },
        );
        const cls =
          (res as { classifications?: Record<string, { category: string }> })
            ?.classifications ?? {};
        for (const [key, v] of Object.entries(cls)) {
          map[key] = v.category as EquipCategory;
        }
      }
      return map;
    },
  });
}

// 34i83: the icon per category lives in one table for every screen (lib/equipmentIcons).
const CAT_ICON = new Proxy({} as Record<EquipCategory, LucideIcon>, { get: (_t, k) => equipIcon(String(k)) });

const todayISO = new Date().toISOString().slice(0, 10);

function fmtDate(d: string | null): string {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
}

export function CustomerEquipment({ custno, locno, onOpenAgreement }: Props) {
  const { data, isLoading } = useCustomerEquipment(custno, locno);
  const { data: installed } = useInstalledEquipment(custno, locno);
  const era = useOwnerEra(custno, locno);
  const aiOverrides = useAiEquipmentOverrides(data ?? []).data;
  const { data: lifecycleOverrides } = useEquipmentOverrides(custno, locno);
  const { data: activeAgrNos } = useActiveAgreementNumbers(custno);
  const activeSet = activeAgrNos ?? new Set<string>();
  const [showReplaced, setShowReplaced] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  // UNIT-INLINE (34i86, Seth: "the pop-up looks atrocious … maybe the equipment
  // rows can expand with nicer looking info and a way to see history/invoice"):
  // one unit open at a time, in place — no modal.
  const [openCounter, setOpenCounter] = useState<string | null>(null);
  // ROOM-EDIT-01 (Seth 2026-10-01: "change a room name… or move one furnace
  // from one room to a different room — Pegasus will be the real system"):
  // rooms are editable, the Pegasus-native way. ESC-mirror units correct
  // through the override rail (ESC untouched); native units edit in place.
  const canEquip = useCan("equipment.edit");
  const roomFields = useEditEquipmentFields();
  const setUnitRoom = useSetUnitRoom();
  const [renamingRoom, setRenamingRoom] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  // Apply the office's Pegasus-native field corrections over the ESC rows
  // (override field wins; null = ESC value) before classifying.
  const mergedData = useMemo(() => {
    if (!data) return [];
    if (!lifecycleOverrides || lifecycleOverrides.size === 0) return data;
    return data.map((r) => {
      const ov = lifecycleOverrides.get(r.counter);
      if (!ov) return r;
      // ROOM-EDIT-01: the office's room correction rides the same merge.
      return { ...r, mfg: ov.mfg ?? r.mfg, model: ov.model ?? r.model, serial: ov.serial ?? r.serial, install_date: ov.install_date ?? r.install_date, eq_type: ov.eq_type ?? r.eq_type, equip_loc: ov.equip_loc ?? r.equip_loc };
    });
  }, [data, lifecycleOverrides]);

  // EQUIP-DEEPLINK (34i84, Seth: "clicking equipment will bring you to the
  // Equipment tab, then clicking on equipment inside Equipment can bring you
  // to the equipment info and the invoice / history of that call"): a
  // ?unit=<counter> on the URL opens that unit's detail here — the Property
  // Timeline and the address chips both land this way — then drops off the
  // URL so closing the detail does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const unitParam = searchParams.get("unit");
  useEffect(() => {
    if (!unitParam || isLoading || !data) return;
    const v = buildEquipmentView(mergedData, aiOverrides, lifecycleOverrides);
    const hit = [...v.systems, ...v.auxiliary, ...v.replaced, ...v.consumables, ...v.materials].find((u) => u.counter === unitParam);
    if (hit) {
      setOpenCounter(hit.counter);
      if (v.replaced.includes(hit)) setShowReplaced(true);
      if (v.materials.includes(hit)) setShowMaterials(true);
      window.setTimeout(() => document.getElementById(`unit-${hit.counter}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    }
    setSearchParams((prev) => { prev.delete("unit"); return prev; }, { replace: true });
  }, [unitParam, isLoading, data, mergedData, aiOverrides, lifecycleOverrides, setSearchParams]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="h-16 animate-pulse bg-slate-100" />
        ))}
      </div>
    );
  }

  const view = buildEquipmentView(mergedData, aiOverrides, lifecycleOverrides);
  const totalReal = view.systems.length + view.auxiliary.length;
  // One unit card, with its panel when it is the open one.
  const unitRow = (e: ClassifiedEquip, extra?: { hideLoc?: boolean; compact?: boolean; muted?: boolean }) => (
    <EquipCardRow
      key={e.counter}
      e={e}
      {...extra}
      onOpen={(x) => setOpenCounter((c) => (c === x.counter ? null : x.counter))}
      expanded={openCounter === e.counter}
      activeAgrNos={activeSet}
      priorOwner={era.priorOwnerFor(e.install_date)}
      panel={openCounter === e.counter ? (
        <UnitPanel
          e={e}
          custno={custno}
          locno={locno}
          hasOverride={!!lifecycleOverrides?.has(e.counter)}
          activeAgrNos={activeSet}
          onCollapse={() => setOpenCounter(null)}
          onOpenAgreement={onOpenAgreement ? () => { setOpenCounter(null); onOpenAgreement(); } : undefined}
        />
      ) : null}
    />
  );

  // EQUIP-ROOM-01 (Seth 2026-09-30, the R&D Batteries / Noddings test): when
  // ESC carries the room each unit serves (equip_loc — "Main", "Basement",
  // "Pool"), the tab reads like the house: one group per room with the whole
  // system together (furnace · coil · condenser · air cleaner · humidifier),
  // instead of one flat pile with the room in small print. Kicks in at 2+
  // rooms; unroomed units keep the classic Systems / Auxiliary sections.
  const activeUnits = [...view.systems, ...view.auxiliary];
  const roomOf = (e: ClassifiedEquip) => (e.equip_loc ?? "").trim();
  const ROOM_CAT_ORDER: EquipCategory[] = [
    "furnace", "air-handler", "boiler", "heat-pump", "ac", "coil", "mini-split", "rooftop",
    "magic-pak", "garage-heater", "water-heater", "erv-hrv", "humidifier", "dehumidifier",
    "air-cleaner", "uv", "thermostat", "rds", "consumable", "material", "other",
  ];
  const roomUnitOrder = (a: ClassifiedEquip, b: ClassifiedEquip) => {
    const tier = (x: ClassifiedEquip) => (x.tier === "auxiliary" ? 1 : 0);
    return tier(a) - tier(b)
      || ROOM_CAT_ORDER.indexOf(a.category) - ROOM_CAT_ORDER.indexOf(b.category)
      || (b.install_date ?? "").localeCompare(a.install_date ?? "");
  };
  const roomNames = [...new Set(activeUnits.map(roomOf).filter(Boolean))];
  const byRoom = roomNames.length >= 2
    ? roomNames
        .map((room) => ({ room, units: activeUnits.filter((e) => roomOf(e) === room).sort(roomUnitOrder) }))
        .sort((a, b) => b.units.length - a.units.length || a.room.localeCompare(b.room))
    : [];
  const roomedCounters = new Set(byRoom.flatMap((g) => g.units.map((u) => u.counter)));
  const looseSystems = view.systems.filter((e) => !roomedCounters.has(e.counter));
  const looseAux = view.auxiliary.filter((e) => !roomedCounters.has(e.counter));

  const installedList = installed ?? [];
  // ROOM-EDIT-01: every room name on the property (ESC + native) — feeds the
  // pick-a-room datalists so moves land on existing names, not typos.
  const roomOptions = [...new Set([...roomNames, ...installedList.map((n) => (n.equip_loc ?? "").trim()).filter(Boolean)])].sort();
  const applyRename = async (room: string) => {
    const to = renameTo.trim();
    if (!to || to === room) { setRenamingRoom(null); return; }
    setRenameBusy(true);
    try {
      const grp = byRoom.find((g) => g.room === room);
      for (const u of grp?.units ?? []) {
        await roomFields.mutateAsync({ esc_counter: u.counter, custno, locno, equip_loc: to });
      }
      for (const n of installedList) {
        if ((n.equip_loc ?? "").trim() === room) {
          await setUnitRoom.mutateAsync({ id: n.id, custno, equip_loc: to });
        }
      }
      setRenamingRoom(null);
    } finally {
      setRenameBusy(false);
    }
  };
  if (totalReal === 0 && view.consumables.length === 0 && view.replaced.length === 0 && view.materials.length === 0 && installedList.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-slate-500">
        No equipment on file for this {locno ? "location" : "customer"} yet.
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {installedList.length > 0 && (
        <Section title="On file with Pegasus" count={installedList.length} hint="install jobs + units the techs added from the field — model + serial verified">
          {installedList.map((e) => (
            <div key={e.id} className="rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-medium text-slate-700">
                  {e.category && <span>{e.category} ·</span>}
                  {e.model ? <CopyCode value={e.model} label="Copy model" variant="text" size="md" mono={false} className="font-semibold text-slate-800" /> : <span>—</span>}
                </span>
                <span className="flex items-center gap-2">
                  {/* EQUIP-ROOM-01: the room the tech/office typed at entry.
                      ROOM-EDIT-01: click it to move/fix — native rows edit in place. */}
                  <NativeRoomChip unit={e} custno={custno} canEdit={canEquip} />
                  {!e.job_id && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">added in the field</span>}
                  {e.install_date && <span className="text-xs text-slate-400">{new Date(e.install_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
                </span>
              </div>
              {e.serial && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                  <span>Serial</span>
                  <CopyCode value={e.serial} label="Copy serial" />
                </div>
              )}
              {/* REFRIG-01: what the installer logged — the next tech knows the charge before they hook up gauges. */}
              {(e.refrigerant || e.charge_oz != null) && (
                <div className="mt-1 text-xs text-slate-500">
                  {[e.refrigerant, e.charge_oz != null ? `${ozText(e.charge_oz)} total charge` : null, e.lineset_ft != null ? `${e.lineset_ft} ft line set` : null].filter(Boolean).join(" · ")}
                </div>
              )}
              {/* QUOTE-POLISH-08: the warranty it was sold with (posted at install close). */}
              {(e.warranty_parts_years != null || e.warranty_labor_years != null) && (
                <div className="mt-1 inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                  Warranty · {e.warranty_parts_years ?? "—"}-yr parts · {e.warranty_labor_years ?? "—"}-yr labor
                  {e.install_date && e.warranty_parts_years != null && (
                    <span className="font-medium text-emerald-600/80"> · parts through {new Date(e.install_date + "T00:00:00").getFullYear() + e.warranty_parts_years}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* EQUIP-ROOM-01 — one group per room the units serve, straight from ESC.
          ROOM-EDIT-01: the pencil renames the room (every unit in it, ESC-mirror
          via override + Pegasus-native in place); per-unit moves live in the
          unit's detail → Edit. */}
      {byRoom.map((g) => (
        <Section
          key={g.room}
          title={g.room}
          count={g.units.length}
          onRename={canEquip ? () => { setRenamingRoom(g.room); setRenameTo(g.room); } : undefined}
        >
          {renamingRoom === g.room && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50/50 px-3 py-2">
              <input
                value={renameTo}
                onChange={(e) => setRenameTo(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") void applyRename(g.room);
                  if (e.key === "Escape") setRenamingRoom(null);
                }}
                placeholder="Room name"
                className="w-44 rounded-md border border-hairline px-2 py-1 text-sm"
              />
              <span className="text-xs text-slate-500">
                renames the room for all {g.units.length} unit{g.units.length === 1 ? "" : "s"} here — saved in Pegasus, ESC untouched
              </span>
              <span className="ml-auto flex gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => setRenamingRoom(null)}>Cancel</Button>
                <Button size="sm" disabled={renameBusy || !renameTo.trim()} onClick={() => void applyRename(g.room)}>
                  <Check className="h-3.5 w-3.5" /> Rename
                </Button>
              </span>
            </div>
          )}
          {g.units.map((e) => unitRow(e, { hideLoc: true }))}
        </Section>
      ))}

      {looseSystems.length > 0 && (
        <Section title={byRoom.length > 0 ? "Other Systems" : "Systems"} count={looseSystems.length}>
          {looseSystems.map((e) => unitRow(e))}
        </Section>
      )}

      {looseAux.length > 0 && (
        <Section title="Auxiliary & indoor air quality" count={looseAux.length}>
          {looseAux.map((e) => unitRow(e))}
        </Section>
      )}

      {view.consumables.length > 0 && (
        <Section title="Consumables" count={view.consumables.length} hint="filters, pads, bulbs — recurring replacement">
          {view.consumables.map((e) => unitRow(e, { compact: true }))}
        </Section>
      )}

      {view.replaced.length > 0 && (
        <Collapsible open={showReplaced} onToggle={() => setShowReplaced((v) => !v)} icon={Layers}
          label={`Replaced / older equipment (${view.replaced.length})`}>
          {view.replaced.map((e) => unitRow(e, { muted: true }))}
        </Collapsible>
      )}

      {view.materials.length > 0 && (
        <Collapsible open={showMaterials} onToggle={() => setShowMaterials((v) => !v)} icon={Wrench}
          label={`Install materials & parts (${view.materials.length})`}>
          <div className="flex flex-wrap gap-1.5 p-1">
            {view.materials.map((e) => (
              <span key={e.counter} className="rounded bg-slate-50 px-2 py-1 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
                {(e.notes?.split(/\r?\n/)[0] || e.mfg || "—").slice(0, 32)}
              </span>
            ))}
          </div>
        </Collapsible>
      )}

      {/* ROOM-EDIT-01: one shared pick-a-room list for every room input. */}
      <datalist id="peg-room-options">
        {roomOptions.map((r) => <option key={r} value={r} />)}
      </datalist>

    </div>
  );
}

/** HD (2026-09-01): real titles instead of whisper-caps — the same header
 *  language as Today v4 / the 360 cards. Count rides as a quiet pill. */
function Section({ title, count, hint, children, onRename }: { title: string; count: number; hint?: string; children: React.ReactNode; onRename?: () => void }) {
  return (
    <div>
      <div className="group/sec mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h3 className="text-[15px] font-bold tracking-tight text-slate-900">{title}</h3>
        <span className="rounded-full bg-slate-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-slate-500">{count}</span>
        {/* ROOM-EDIT-01: rename the room — shows on hover, like the card pencils. */}
        {onRename && (
          <button
            type="button"
            onClick={onRename}
            title="Rename this room — every unit here moves with it (saved in Pegasus)"
            className="self-center rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:text-brand-600 focus-visible:opacity-100 group-hover/sec:opacity-100"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/** ROOM-EDIT-01 — the room chip on a Pegasus-native unit: click, type, Enter.
 *  Edits pegasus_equipment directly (this IS the system of record for these). */
function NativeRoomChip({ unit, custno, canEdit }: { unit: InstalledEquipment; custno: string; canEdit: boolean }) {
  const setRoom = useSetUnitRoom();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(unit.equip_loc ?? "");
  if (!canEdit && !unit.equip_loc) return null;
  const save = () => setRoom.mutate(
    { id: unit.id, custno, equip_loc: val.trim() || null },
    { onSuccess: () => setEditing(false) },
  );
  if (!editing) {
    return (
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => { setVal(unit.equip_loc ?? ""); setEditing(true); }}
        title={canEdit ? "Change the room this unit serves" : undefined}
        className={cn(
          "rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600",
          canEdit && "transition-colors hover:bg-brand-50 hover:text-brand-700",
          !unit.equip_loc && "text-slate-400",
        )}
      >
        {unit.equip_loc ?? "+ room"}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        autoFocus
        list="peg-room-options"
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        placeholder="Room"
        className="w-28 rounded-md border border-hairline px-1.5 py-0.5 text-[11px]"
      />
      <button type="button" onClick={save} disabled={setRoom.isPending} title="Save"
        className="rounded p-0.5 text-emerald-600 transition-colors hover:bg-emerald-50">
        <Check className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function Collapsible({ open, onToggle, icon: Icon, label, children }: {
  open: boolean; onToggle: () => void; icon: LucideIcon; label: string; children: React.ReactNode;
}) {
  return (
    <div>
      <button type="button" onClick={onToggle}
        className="inline-flex items-center gap-1.5 rounded-md py-1 text-[13px] font-semibold text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40">
        <ChevronRight className={cn("h-4 w-4 text-slate-400 transition-transform", open && "rotate-90")} />
        <Icon className="h-4 w-4 text-slate-400" />
        {label}
      </button>
      {open && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

function warrantyInline(e: ClassifiedEquip): { text: string; expired: boolean } | null {
  if (e.warrantyParts || e.warrantyLabor) {
    const parts: string[] = [];
    if (e.warrantyLabor) parts.push(`Labor ${fmtDate(e.warrantyLabor)}`);
    if (e.warrantyParts) parts.push(`Parts ${fmtDate(e.warrantyParts)}`);
    const latest = e.warrantyParts ?? e.warrantyLabor!;
    return { text: parts.join(" · "), expired: latest < todayISO };
  }
  if (e.warrantyEffective) {
    const expired = e.warrantyEffective < todayISO;
    return { text: `Warranty ${expired ? "expired" : "to"} ${fmtDate(e.warrantyEffective)}`, expired };
  }
  return null;
}

// Heating equipment (furnace / boiler / garage heater) reads WARM (orange) to
// set heat apart from cooling at a glance (Seth 2026-06-01); since 34i83 the
// families (heating · cooling · water · air · controls) come from one table.
function catTint(category: EquipCategory, muted?: boolean): string {
  if (muted) return "bg-slate-100 text-slate-400";
  // 34i83: the same family colors the timeline and the address chips use.
  return equipStyle(category).tile;
}

export function EquipCardRow({ e, muted, compact, hideLoc, onOpen, onToggle, checked, activeAgrNos, priorOwner, expanded, panel }: {
  e: ClassifiedEquip; muted?: boolean; compact?: boolean; onOpen?: (e: ClassifiedEquip) => void;
  onToggle?: () => void; checked?: boolean;
  /** EQUIP-ROOM-01: inside a room group the room name is the section title — skip the chip. */
  hideLoc?: boolean;
  activeAgrNos: Set<string>;
  /** When this unit was installed under a prior owner (by install_date vs the handoff). */
  priorOwner?: string | null;
  /** UNIT-INLINE: this card is the open one — the chevron turns and `panel` renders inside it. */
  expanded?: boolean;
  panel?: ReactNode;
}) {
  const selectMode = !!onToggle;
  const agreementActive = !!e.ser_agr_no && activeAgrNos.has(e.ser_agr_no);
  const Icon = CAT_ICON[e.category];
  const title = e.model || e.mfg || e.categoryLabel;
  const sub = [e.categoryLabel, e.mfg && e.mfg !== title ? e.mfg : null].filter(Boolean).join(" · ");
  const w = warrantyInline(e);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <Card
      id={`unit-${e.counter}`}
      className={cn(
        "group overflow-hidden p-0 transition-all",
        !selectMode && "hover:border-brand-300 hover:shadow-sm",
        expanded && "border-brand-300 shadow-e2 ring-1 ring-brand-500/10",
        muted && "bg-slate-50/60",
      )}
    >
    <div
      role={selectMode ? undefined : "button"}
      tabIndex={selectMode ? undefined : 0}
      aria-expanded={selectMode ? undefined : !!expanded}
      onClick={selectMode ? undefined : () => onOpen?.(e)}
      onKeyDown={selectMode ? undefined : (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onOpen?.(e); } }}
      className={cn(
        "flex items-start gap-3 p-3",
        !selectMode && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40",
      )}
    >
      {selectMode && (
        <input type="checkbox" checked={!!checked} onChange={onToggle} aria-label="Covered by this plan"
          className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
      )}
      <span className={cn("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg", catTint(e.category, muted))}>
        <Icon className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {/* COPY-01: the model number IS the headline — click/tap/hold copies it. */}
          {e.model ? (
            <CopyCode value={e.model} label="Copy model" variant="text" size="md" mono={false}
              className={cn("font-semibold", muted ? "text-slate-500" : "text-slate-900")} />
          ) : (
            <span className={cn("truncate text-sm font-semibold", muted ? "text-slate-500" : "text-slate-900")}>{title}</span>
          )}
          {!compact && sub && <span className="text-xs text-slate-500">{sub}</span>}
          {e.ser_agr_no && (
            agreementActive ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700">
                <ShieldCheck className="h-3 w-3" /> Agreement
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                Past agreement
              </span>
            )
          )}
          {e.duplicateOf && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">possible duplicate</span>
          )}
          {e.replaced && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              {e.deletedInEsc ? "deleted in ESC" : "replaced"}{e.replacedDate ? ` ${fmtDate(e.replacedDate)}` : ""}
            </span>
          )}
          <PriorOwnerBadge name={priorOwner} />
        </div>

        {!compact && (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {e.serial && (
              <span className="inline-flex items-center gap-1">
                <span>SN</span>
                <CopyCode value={e.serial} label="Copy serial" className={muted ? "opacity-70" : undefined} />
              </span>
            )}
            {installDateLabel(e.install_date) && <span>Installed {installDateLabel(e.install_date)}</span>}
            {e.soldDate && installDateLabel(e.soldDate) && <span title="Charged on an earlier invoice, installed later — one unit, two invoices">Sold {installDateLabel(e.soldDate)}</span>}
            {!hideLoc && e.equip_loc && <span>{e.equip_loc}</span>}
            {w && <span className={w.expired ? "text-slate-400" : "text-emerald-700"}>{w.text}</span>}
          </div>
        )}
      </div>

      {!selectMode && <Chevron className={cn("h-4 w-4 flex-shrink-0 transition-all", expanded ? "text-brand-500" : "text-slate-300 group-hover:translate-x-0.5 group-hover:text-brand-500")} />}
    </div>
    {expanded && panel}
    </Card>
  );
}

// ----------------------------------------------------------------------------
// UNIT-INLINE (34i86) — the unit's panel, in its own card. Replaces the EQUIP-02
// modal (Seth: "the pop-up looks atrocious"). Facts · the paper trail (install
// and sale invoices, with the call) · the calls that mention this unit · notes
// · the office's actions (edit, mark removed, correct a guess). Every link goes
// to a page — the invoice, the call, the Service tab — never another pop-up.
// ----------------------------------------------------------------------------
const CALL_WORDS: Partial<Record<EquipCategory, RegExp>> = {
  furnace: /\bfurn(ace)?\b|\bfrn\b|heat exchanger|ign[io]t[eo]r|inducer|no heat|\bheating\b|blower/i,
  ac: /\ba\/?c\b|air cond|condens|refrigerant|capacitor|no cool|cooling|compressor|contactor/i,
  coil: /\bcoil\b|\bevap/i,
  "heat-pump": /heat pump|\bhp\b/i,
  "mini-split": /mini.?split|ductless/i,
  boiler: /boiler|radiator|baseboard|hydronic/i,
  "garage-heater": /garage|unit heater|modine/i,
  "water-heater": /water heater|\bwh\b|hot water/i,
  humidifier: /humidif|aprilaire|\bapr\d*\b|water panel|\bpad\b/i,
  dehumidifier: /dehumid/i,
  uv: /\buv\b|scrubber|purif/i,
  "air-cleaner": /air clean|hepa|breathe|\bfilter/i,
  thermostat: /thermostat|t-?stat|\bnest\b|ecobee/i,
  "erv-hrv": /\berv\b|\bhrv\b|ventilat/i,
  "air-handler": /air handler/i,
};
/** The calls whose write-up names this unit — by model, or by the words a tech uses for its kind. */
function callsForUnit(rows: CustomerDispatchRow[], e: ClassifiedEquip): CustomerDispatchRow[] {
  const model = (e.model ?? "").trim();
  const modelRe = model.length >= 4 ? new RegExp(model.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i") : null;
  const words = CALL_WORDS[e.category] ?? null;
  return rows.filter((r) => {
    const t = r.notes ?? "";
    if (!t) return false;
    if (modelRe && modelRe.test(t)) return true;
    return !!words && words.test(t);
  });
}
const money = (n: number | null | undefined) =>
  n == null ? null : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const dayLabel = (iso: string | null | undefined) =>
  iso ? new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

interface InvoiceBrief { invoice: string; inv_date: string | null; inv_amount: number | null; dispatch: string | null; salesman: string | null }
function useInvoiceBriefs(invoices: string[]) {
  return useQuery({
    queryKey: ["invoice_briefs", invoices.join(",")],
    enabled: invoices.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<InvoiceBrief[]> => {
      const { data, error } = await supabase.from("mirror_sales").select("invoice, inv_date, inv_amount, dispatch, salesman").in("invoice", invoices);
      if (error) throw error;
      return (data ?? []) as InvoiceBrief[];
    },
  });
}

function Fact({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <dt className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-400">{label}</dt>
      <dd className="mt-0.5 truncate text-[13px] font-medium text-slate-800">{children}</dd>
    </div>
  );
}
function PanelLabel({ children, count }: { children: ReactNode; count?: number | null }) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
      {children}{count != null && <span className="font-semibold tabular-nums text-slate-300">{count}</span>}
    </div>
  );
}
function TrailRow({ icon: Icon, title, meta, onClick, tone = "slate" }: { icon: LucideIcon; title: ReactNode; meta?: ReactNode; onClick: () => void; tone?: "slate" | "brand" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group/row flex w-full items-center gap-2.5 rounded-lg border border-hairline bg-white px-2.5 py-2 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
    >
      <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-md", tone === "brand" ? "bg-brand-50 text-brand-600" : "bg-slate-100 text-slate-500")}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-slate-800">{title}</span>
        {meta && <span className="block truncate text-[11.5px] text-slate-500">{meta}</span>}
      </span>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300 transition-all group-hover/row:translate-x-0.5 group-hover/row:text-brand-500" />
    </button>
  );
}

function UnitPanel({ e, custno, locno, hasOverride, onCollapse, onOpenAgreement, activeAgrNos }: {
  e: ClassifiedEquip; custno: string; locno?: string | null; hasOverride?: boolean;
  onCollapse: () => void; onOpenAgreement?: () => void; activeAgrNos: Set<string>;
}) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  // 360-AUDIT wave 2 (C): equipment edits + lifecycle marks are record writes
  // — grantable on their own key at rollout (viewing stays for everyone).
  const canEquip = useCan("equipment.edit");
  const setOverride = useSetEquipmentOverride();
  const clearOverride = useClearEquipmentOverride();
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState<RemovalReason>("warranty");
  const [date, setDate] = useState(todayISO);
  const editFields = useEditEquipmentFields();
  const [editing, setEditing] = useState(false);
  const [fMfg, setFMfg] = useState(""); const [fModel, setFModel] = useState("");
  const [fSerial, setFSerial] = useState(""); const [fInstall, setFInstall] = useState(""); const [fType, setFType] = useState("");
  const [fLoc, setFLoc] = useState(""); // ROOM-EDIT-01: move the unit to a different room
  const onFormKeys = useEntryFormKeys();
  const invoiceNos = [e.invoice, e.soldInvoice].filter((x): x is string => !!x).filter((x, i, a) => a.indexOf(x) === i);
  const { data: briefs } = useInvoiceBriefs(invoiceNos);
  const { data: dispatches } = useCustomerDispatches(custno);
  const calls = useMemo(() => callsForUnit(dispatches ?? [], e), [dispatches, e]);
  const [allCalls, setAllCalls] = useState(false);

  const startEdit = () => { setFMfg(e.mfg ?? ""); setFModel(e.model ?? ""); setFSerial(e.serial ?? ""); setFInstall(e.install_date ?? ""); setFType(e.eq_type ?? ""); setFLoc(e.equip_loc ?? ""); setEditing(true); };
  const saveEdit = () => editFields.mutate({ esc_counter: e.counter, custno, locno, mfg: fMfg.trim() || null, model: fModel.trim() || null, serial: fSerial.trim() || null, install_date: fInstall || null, eq_type: fType.trim() || null, equip_loc: fLoc.trim() || null }, { onSuccess: () => setEditing(false) });
  const agreementActive = !!e.ser_agr_no && activeAgrNos.has(e.ser_agr_no);
  const w = warrantyInline(e);
  const briefFor = (inv: string | null | undefined) => (briefs ?? []).find((b) => b.invoice === inv) ?? null;
  const techFor = (dispatch: string | null | undefined) => (dispatches ?? []).find((d) => d.dispatch === dispatch)?.tech_name ?? null;
  const install = briefFor(e.invoice);
  const sale = e.soldInvoice && e.soldInvoice !== e.invoice ? briefFor(e.soldInvoice) : null;
  const inputCls = "w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";
  const shownCalls = allCalls ? calls : calls.slice(0, 4);

  return (
    <div className="border-t border-hairline bg-slate-50/50 px-4 py-3.5" data-entry-form onKeyDown={onFormKeys} onClick={(ev) => ev.stopPropagation()}>
      {/* FACTS — or the edit form in their place */}
      {editing ? (
        <div className="space-y-2.5">
          <PanelLabel>Details <InfoTip label="About editing details">Correct the manufacturer, model, serial, install date, type or room — saved in Pegasus (ESC untouched) and shown everywhere. A blank field uses the ESC value.</InfoTip></PanelLabel>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <label className="block text-xs"><span className="mb-0.5 block text-slate-500">Manufacturer</span><input value={fMfg} onChange={(ev) => setFMfg(ev.target.value)} className={inputCls} /></label>
            <label className="block text-xs"><span className="mb-0.5 block text-slate-500">Model</span><input value={fModel} onChange={(ev) => setFModel(ev.target.value)} className={inputCls} /></label>
            <label className="block text-xs"><span className="mb-0.5 block text-slate-500">Serial</span><input value={fSerial} onChange={(ev) => setFSerial(ev.target.value)} className={cn(inputCls, "font-mono")} /></label>
            <label className="block text-xs"><span className="mb-0.5 block text-slate-500">Type</span><input value={fType} onChange={(ev) => setFType(ev.target.value)} className={inputCls} /></label>
            <label className="block text-xs"><span className="mb-0.5 block text-slate-500">Installed</span><input type="date" value={fInstall} onChange={(ev) => setFInstall(ev.target.value)} className={inputCls} /></label>
            <label className="block text-xs"><span className="mb-0.5 block text-slate-500">Room / Area</span><input list="peg-room-options" value={fLoc} onChange={(ev) => setFLoc(ev.target.value)} placeholder="Main · Basement · Pool…" className={inputCls} /></label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" disabled={editFields.isPending} onClick={saveEdit}><Check className="h-3.5 w-3.5" /> Save</Button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
          {e.model && <Fact label="Model"><CopyCode value={e.model} label="Copy model" size="md" /></Fact>}
          {e.serial && <Fact label="Serial"><CopyCode value={e.serial} label="Copy serial" size="md" /></Fact>}
          {installDateLabel(e.install_date) && <Fact label="Installed">{installDateLabel(e.install_date)}</Fact>}
          {e.equip_loc && <Fact label="Room">{e.equip_loc}</Fact>}
          {w && <Fact label="Warranty" className={w.expired ? "" : ""}><span className={w.expired ? "text-slate-500" : "text-emerald-700"}>{w.text}</span></Fact>}
          {e.ser_agr_no && (
            <Fact label="Agreement">
              {onOpenAgreement ? (
                <button type="button" onClick={onOpenAgreement} className={cn("inline-flex items-center gap-1 hover:underline", agreementActive ? "text-brand-700" : "text-slate-500")}>
                  {agreementActive && <ShieldCheck className="h-3.5 w-3.5" />}{agreementActive ? `On ${e.ser_agr_no}` : `Past · ${e.ser_agr_no}`}
                </button>
              ) : (agreementActive ? `On ${e.ser_agr_no}` : `Past · ${e.ser_agr_no}`)}
            </Fact>
          )}
          {e.soldDate && installDateLabel(e.soldDate) && <Fact label="Sold">{installDateLabel(e.soldDate)}</Fact>}
          {e.eq_type && <Fact label="ESC Type">{e.eq_type}</Fact>}
          {e.replaced && (
            <Fact label="Status">
              {e.deletedInEsc ? (
                <>
                  <span className="text-amber-700">Deleted in ESC{e.replacedDate ? ` · last on the ESC record ${fmtDate(e.replacedDate)}` : ""}</span>
                  <span className="ml-1 text-[11px] font-normal text-slate-400">· from the ESC sync</span>
                </>
              ) : (
                <>
                  <span className="text-amber-700">Replaced{e.replacedDate ? ` ${fmtDate(e.replacedDate)}` : ""}{e.replacedByModel ? ` · by ${e.replacedByModel}` : ""}</span>
                  <span className="ml-1 text-[11px] font-normal text-slate-400">{e.overrideSet ? "· set by office" : "· auto-detected"}</span>
                </>
              )}
            </Fact>
          )}
          {e.duplicateOf && <Fact label="Status"><span className="text-amber-700">Possible duplicate record</span></Fact>}
        </dl>
      )}

      {/* PAPER TRAIL — the invoices that put it in, with the call */}
      {(e.invoice || sale) && !editing && (
        <div className="mt-4">
          <PanelLabel>Paper Trail</PanelLabel>
          <div className="space-y-1.5">
            {e.invoice && (
              <TrailRow
                icon={Receipt}
                tone="brand"
                title={<>Install Invoice <span className="font-mono text-[12px] font-medium text-slate-500">#{e.invoice}</span></>}
                meta={[dayLabel(install?.inv_date) ?? installDateLabel(e.install_date), money(install?.inv_amount), techFor(install?.dispatch)].filter(Boolean).join(" · ") || null}
                onClick={() => navigate(`/invoice/${e.invoice}`)}
              />
            )}
            {sale && (
              <TrailRow
                icon={Receipt}
                title={<>Sale Invoice <span className="font-mono text-[12px] font-medium text-slate-500">#{e.soldInvoice}</span></>}
                meta={[dayLabel(sale.inv_date) ?? installDateLabel(e.soldDate), money(sale.inv_amount), techFor(sale.dispatch), "charged before the install"].filter(Boolean).join(" · ")}
                onClick={() => navigate(`/invoice/${e.soldInvoice}`)}
              />
            )}
            {install?.dispatch && (
              <TrailRow
                icon={PhoneCall}
                title="The Install Call"
                meta={[dayLabel((dispatches ?? []).find((d) => d.dispatch === install.dispatch)?.appointment_date), techFor(install.dispatch), `dispatch ${install.dispatch}`].filter(Boolean).join(" · ")}
                onClick={() => navigate(`/dispatch/${install.dispatch}`)}
              />
            )}
          </div>
        </div>
      )}

      {/* CALLS THAT MENTION THIS UNIT */}
      {!editing && calls.length > 0 && (
        <div className="mt-4">
          <PanelLabel count={calls.length}>Calls Mentioning This Unit</PanelLabel>
          <ul className="divide-y divide-hairline/70 overflow-hidden rounded-lg border border-hairline bg-white">
            {shownCalls.map((c) => (
              <li key={c.dispatch}>
                <button
                  type="button"
                  disabled={!!c.peg}
                  onClick={() => navigate(`/dispatch/${c.dispatch}`)}
                  className="group/row flex w-full items-start gap-3 px-2.5 py-2 text-left transition-colors hover:bg-brand-50/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40 disabled:cursor-default disabled:hover:bg-transparent"
                >
                  <span className="w-[84px] shrink-0 pt-px text-[12px] tabular-nums text-slate-500">{dayLabel(c.appointment_date) ?? "—"}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-slate-800">{(c.notes ?? "").replace(/\s+/g, " ").trim() || "Service call"}</span>
                    {(c.tech_name || c.status) && <span className="block truncate text-[11.5px] text-slate-500">{[c.tech_name, c.status].filter(Boolean).join(" · ")}</span>}
                  </span>
                  {!c.peg && <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-300 transition-all group-hover/row:translate-x-0.5 group-hover/row:text-brand-500" />}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-1.5 flex items-center justify-between">
            {calls.length > 4 ? (
              <button type="button" onClick={() => setAllCalls((v) => !v)} className="text-[12px] font-semibold text-brand-700 hover:text-brand-900">
                {allCalls ? "Fewer" : `All ${calls.length}`}
              </button>
            ) : <span />}
            <button
              type="button"
              onClick={() => setSearchParams((prev) => { prev.set("tab", "service"); return prev; })}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 hover:text-brand-900"
            >
              Every Call <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* NOTES */}
      {!editing && e.notes && e.notes.trim() && (
        <div className="mt-4">
          <PanelLabel>Notes</PanelLabel>
          <p className="whitespace-pre-wrap rounded-lg border border-hairline bg-white px-3 py-2 text-[12.5px] leading-snug text-slate-700">{e.notes.trim()}</p>
        </div>
      )}

      {/* ACTIONS — the office's say */}
      {!editing && (
        removing ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <PanelLabel>Mark Removed / Replaced</PanelLabel>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="mb-0.5 block text-slate-500">Reason</span>
                <select value={reason} onChange={(ev) => setReason(ev.target.value as RemovalReason)} className={inputCls}>
                  {(Object.keys(REASON_LABEL) as RemovalReason[]).map((r) => (
                    <option key={r} value={r}>{REASON_LABEL[r]}</option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                <span className="mb-0.5 block text-slate-500">Removed Date</span>
                <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} className={inputCls} />
              </label>
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setRemoving(false)}>Cancel</Button>
              <Button size="sm" disabled={setOverride.isPending}
                onClick={() => setOverride.mutate(
                  { esc_counter: e.counter, custno, locno, status: "removed", removal_reason: reason, removal_date: date },
                  { onSuccess: () => { setRemoving(false); onCollapse(); } },
                )}>
                <Check className="h-3.5 w-3.5" /> Confirm Removed
              </Button>
            </div>
          </div>
        ) : canEquip ? (
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-hairline/70 pt-3">
            {hasOverride && !e.deletedInEsc && (
              <Button variant="ghost" size="sm" className="mr-auto text-slate-500" disabled={clearOverride.isPending}
                onClick={() => clearOverride.mutate({ esc_counter: e.counter, custno }, { onSuccess: onCollapse })}>
                Use Auto-Detected
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={startEdit}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
            {!e.replaced ? (
              <Button variant="secondary" size="sm" onClick={() => setRemoving(true)}>
                <Layers className="h-3.5 w-3.5" /> Mark Removed / Replaced
              </Button>
            ) : (
              <Button variant="secondary" size="sm" disabled={setOverride.isPending}
                onClick={() => setOverride.mutate({ esc_counter: e.counter, custno, locno, status: "active" }, { onSuccess: onCollapse })}>
                <Check className="h-3.5 w-3.5" /> Still Active
              </Button>
            )}
          </div>
        ) : null
      )}
    </div>
  );
}
